import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { submitProductInterest } from '@/hooks/useProductInterest';
import { supabase } from '@/integrations/supabase/client';

interface ProductInterestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productTitle: string;
  productId: string;
  producerId: string;
}

export function ProductInterestDialog({
  open,
  onOpenChange,
  productTitle,
  productId,
  producerId,
}: ProductInterestDialogProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill email from logged-in user
  useEffect(() => {
    if (!open) return;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user?.email && !email) {
        setEmail(user.email);
      }
    });
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSubmitting(true);
    setError(null);

    const result = await submitProductInterest({
      productId,
      producerId,
      email: email.trim(),
      name: name.trim() || undefined,
      message: message.trim() || undefined,
    });

    setSubmitting(false);

    if (result.success) {
      setSubmitted(true);
    } else {
      setError(result.error || 'Något gick fel. Försök igen.');
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setName('');
      setEmail('');
      setMessage('');
      setSubmitted(false);
      setError(null);
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {submitted ? (
          <>
            <DialogHeader>
              <DialogTitle>Vi hör av oss!</DialogTitle>
              <DialogDescription>
                Du får ett mejl så fort produkten finns igen. Inget annat, vi lovar.
              </DialogDescription>
            </DialogHeader>
            <Button onClick={handleClose} variant="secondary" className="mt-4">
              Stäng
            </Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Vill du bli meddelad när den finns?</DialogTitle>
              <DialogDescription>
                Lämna din e-post så säger vi till när du kan beställa.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="interest-email">E-post</Label>
                <Input
                  id="interest-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="din@epost.se"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="interest-name">Namn <span className="text-muted-foreground text-xs">(valfritt)</span></Label>
                <Input
                  id="interest-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ditt namn"
                />
              </div>
              <div>
                <Label htmlFor="interest-message">Något du vill tillägga? <span className="text-muted-foreground text-xs">(valfritt)</span></Label>
                <Textarea
                  id="interest-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="T.ex. önskad mängd"
                  rows={2}
                />
              </div>
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
              <div className="space-y-2">
                <Button type="submit" disabled={submitting || !email.trim()} className="w-full">
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Säg till mig
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Du får bara ett mejl när den finns igen.
                </p>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
